import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { global } from "@shared/libs/Global";
import SearchPanel from "@widgets/SearchPanel";
import SnTree from "@shared/components/Grid/SnTree";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnResizable from "@shared/components/SnResizable";
import { getInitPopUp } from "@shared/utils/CommonUtils";
import { Button } from "@mui/material";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import SharedForm from "@widgets/SharedForm";
import { showDialog } from "@widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import CommonSpinner from "@widgets/CommonSpinner";

interface IssueMenuPopupProps {
  code: string;
  selectedData?: any;
  modalRef?: any;
  autoSearch?: boolean;
}

const IssueMenuPopup = forwardRef(
  (
    { code, selectedData, modalRef, autoSearch = true }: IssueMenuPopupProps,
    ref
  ) => {
    const { userInfo } = global();

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any[]>([]);
    const [sharedForm, setSharedForm] = useState<any[]>([]);
    const [treeDataSource, setTreeDataSource] = useState<any[]>([]);
    const [focusedRowData, setFocusedRowData] = useState<any>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [btnText, setBtnText] = useState<string>("Expand");
    const [gridFocus, setGridFocus] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const searchRef = useRef<any>(null);
    const treeGridApiRef = useRef<any>(null);
    const sharedFormRef = useRef<any>();

    //#region panel 사이즈 관련
    const cd_menu = "IssueMenuPopup";
    const defaultSize = { panel1width: "25%", panel2width: "75%" };
    const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize); // SnResizable들의 크기

    //#endregion

    const autoGroupColumnDef = {
      headerName: "메뉴", // 그룹 컬럼의 헤더 이름
      field: "nm_menu", // 데이터를 표시할 필드
      flex: 1,
      filter: "agTextColumnFilter",
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭 방지
        suppressHoverHighlight: false,
      },
    };

    useImperativeHandle(ref, () => ({
      getSelectedRow: () => {
        return returnData();
      },
      onSearch,
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUp(code, {});
          setFormData(rtn.formData);
          setFormControlData(rtn.formControlData);
          setSharedForm(rtn.form.freeform1);

          // 서버에서 받은 컬럼 중 nm_menu는 숨김
          const hideColumns = rtn.grid.grid1.map((col: any) => {
            if (col.cd_column !== "nm_menu") {
              return { ...col, hide: true };
            }
            return col;
          });
          setColums(hideColumns);

          onSearch(rtn.formData);
        } catch (error) {
          console.log(error);
        }
      };
      fetchform();
    }, [userInfo]);

    useEffect(() => {
      const str_search = searchRef.current.getFormdata()?.str_search;
      let selectedRow;
      if (str_search) {
        // str_search를 메뉴코드로 가지고 있는 노드 찾아서 focus
        treeGridApiRef.current?.api?.forEachNode((node: any) => {
          if (node.data?.cd_menu === str_search) {
            treeGridApiRef.current.selectAndFocusRow(node.rowIndex);
            selectedRow = node.data;
          } else {
            treeGridApiRef.current.selectAndFocusRow(0);
            selectedRow =
              treeGridApiRef.current.api?.getSelectedNodes()[0]?.data;
          }
        });
      } else {
        // 제일 첫번째 노드 focus
        treeGridApiRef.current.selectAndFocusRow(0);
        selectedRow = treeGridApiRef.current.api?.getSelectedNodes()[0]?.data;
      }

      if (selectedRow) {
        setFocusedRowData(selectedRow);
        treeGridApiRef.current?.api.collapseAll();
      }
      setGridFocus(false);
    }, [gridFocus]);

    const onSearch = async (formData: any) => {
      setIsLoading(true);
      const fetchdatasource = async () => {
        try {
          const params = {
            id: code.toLowerCase() + "_01",
            params: { ...formData, ...searchRef.current.getFormdata() },
          };

          setTreeDataSource([]);

          const data: any = await fnGetDataSet(params);
          console.log(data[0]);

          const treeData = buildTreeData(data[0] || []);
          setTreeDataSource(treeData);
          setIsInitialized(true);

          setGridFocus(true);
        } catch (error) {
          console.log(error);
        }
      };
      await fetchdatasource();
      setIsLoading(false);
    };

    const buildTreeData = (flatData: any[]) => {
      if (!flatData) return [];

      // `cd_menu`를 키로, 항목을 값으로 하는 Map 생성
      const menuMap = new Map(
        flatData.map((item: any) => [item.cd_menu, item])
      );

      return flatData.map((item: any) => {
        const path_arr: string[] = [];
        let currentItem: any = item;

        while (currentItem) {
          // 부모가 없는 경우 → 루트
          if (!currentItem.cd_menu_parent) {
            path_arr.unshift(currentItem.nm_menu);
            break;
          }

          // 자기 자신 추가
          path_arr.unshift(currentItem.nm_menu);

          // 부모 찾기
          currentItem = menuMap.get(currentItem.cd_menu_parent);
        }

        return {
          ...item,
          path_arr,
        };
      });
    };

    const onRowClicked = (params: any) => {
      if (params.node && params.data) {
        setFocusedRowData(params.data);
      }
    };

    const returnData = async () => {
      const param = {
        cd_firm: userInfo.cd_firm,
        cd_menu: focusedRowData.cd_menu,
        fg_item: formData.fg_item,
      };

      const fg_menu = focusedRowData.fg_menu;
      if (fg_menu == "REG" || fg_menu == "REP" || fg_menu == "MGM") {
        const result = await callApi("checkMenu", param);
        console.log(result);
        if (result.status) {
          const data = result.data;
          if (data.length > 0) {
            const no_issue = data[0].no_issue;
            const nm_emp = data[0].nm_emp;
            await showDialog(
              "alter",
              "진행중인 이슈가 있습니다. " + no_issue + "(" + nm_emp + ")"
            );
          }
          return [focusedRowData];
        }
      }
    };

    const onHeaderBtnsClick = (id: string) => {
      if (id === "btnExpColl") {
        if (btnText === "Collapse") {
          // 현재 열려있는 상태 → 닫기
          treeGridApiRef.current?.api.collapseAll();
          setBtnText("Expand");

          // 첫번째 노드 focus
          treeGridApiRef.current.selectAndFocusRow(0);
          const selectedRow =
            treeGridApiRef.current.api?.getSelectedNodes()[0]?.data;

          if (selectedRow) {
            setFocusedRowData(selectedRow);
          }
        } else {
          // 현재 닫혀있는 상태 → 열기
          treeGridApiRef.current?.api.expandAll();
          setBtnText("Collapse");
        }
      }
    };

    const onFormButtonClick = async (id: string) => {
      if (id === "btn_save") {
        try {
          const requiredValuesCheck =
            treeGridApiRef.current.requiredValuesCheck();
          if (!requiredValuesCheck) {
            return;
          }

          const treeGetChanges = treeGridApiRef.current.getChange();
          if (treeGetChanges.modifiedRowcount == 0) {
            await showDialog("alter", "변경된 내용이 없습니다.");
            return;
          }

          if (treeGetChanges.update) {
            treeGetChanges.update = treeGetChanges.update.map((row: any) => ({
              ...row,
              cd_user_amd: userInfo.cd_user,
            }));
          }

          const saveData = {
            sys_notesmenu: treeGetChanges,
          };
          await fnSave("SN_SYS_BAS_REG_004_SAVE_01", saveData);
          treeGridApiRef.current.resetUpdate();
        } catch (error: any) {}
      }
    };

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <CommonSpinner isLoading={isLoading} />
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => {
              onSearch({});
            }}
            sx={{
              width: 100,
              marginTop: 2,
            }}
          >
            조회
          </Button>
        </div>

        <div className="w-full flex h-full min-h-0 space-x-1.5">
          <SnResizable
            direction={"width"}
            seq={1}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel1width}
            onResizeStop={onResizeStop}
            enable={{ right: true }}
            minWidth={130}
          >
            <SnTree
              ref={treeGridApiRef}
              rowData={treeDataSource}
              columnDefs={columns}
              rowSelection="single"
              onRowClicked={onRowClicked}
              suppressNoRowsOverlay={true}
              autoGroupColumnDef={autoGroupColumnDef} // 컬럼설정하는거
              animateRows={false} // 애니메이션 비활성화
              groupDefaultExpanded={-1} // 디폴트로 열림 상태
              getDataPath={(data) => data.path_arr}
            />
          </SnResizable>
          <SnResizable
            direction={"width"}
            seq={2}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel2width}
            onResizeStop={onResizeStop}
            enable={{ right: true }}
            minWidth={130}
          >
            <SharedForm
              ref={sharedFormRef}
              data={focusedRowData || {}}
              controls={sharedForm}
              gridref={treeGridApiRef}
              title="상세내용"
              header={
                <div>
                  <SnButton
                    text={btnText}
                    onClick={() => {
                      onHeaderBtnsClick("btnExpColl");
                    }}
                  />
                </div>
              }
              onButtonClick={onFormButtonClick}
            ></SharedForm>
          </SnResizable>
        </div>
      </div>
    );
  }
);

export default IssueMenuPopup;
